import axios from 'axios';
import { EcommerceItem, EcommerceObject } from './analytics';

export type EventData = {
  actionName?: string;
  pageUrl?: string;
  visitorId?: string;
  eventCategory?: string; // i.e Video, Music, Games
  eventAction?: string; // i.e Play, Pause, Stop, Skip, Rewind, Fast Forward
  eventName?: string; // i.e Video Title, Music Title, Game Title
  eventValue?: number; // i.e 100, 200, 300
  //TODO add performance metrics
  items?: EcommerceItem[];
  orderValue?: number;
  contentName?: string;
  contentPiece?: string;
  contentTarget?: string;
  contentAction?: string;
};

export type MatomoItem = {
  productSKU: string;
  productName?: string;
  categoryName?: string;
  price?: number;
  quantity?: number;
};

export type MatomoEcommerce = {
  grandTotal: number;
  orderId: string;
};

const convertItemsToMatomoParams = (items: EcommerceItem[]) => {
  return `[${items
    .map((item) => `["${item.item_id}","${item.item_name}","${item.item_category}","${item.price}","${item.quantity}"]`)
    .join(',')}]`;
};

export const trackEvent = (event: EventData) => {
  const params = new URLSearchParams();

  // Add event data as query parameters if they exist
  const date = new Date();
  params.append('idsite', process.env.REACT_APP_MATOMO_ANALYTICS_ID_SITE ?? '');
  params.append('rec', '1');
  params.append('rand', Math.random().toString());
  params.append('h', date.getHours().toString());
  params.append('m', date.getMinutes().toString());
  params.append('s', date.getSeconds().toString());
  if (event.actionName) params.append('action_name', event.actionName);
  if (event.pageUrl) params.append('url', event.pageUrl);
  if (event.visitorId) params.append('_id', event.visitorId);
  if (event.eventCategory) params.append('e_c', event.eventCategory);
  if (event.eventAction) params.append('e_a', event.eventAction);
  if (event.eventName) params.append('e_n', event.eventName);
  if (event.eventValue) params.append('e_v', event.eventValue.toString());
  if (event.items && event.items.length > 0) params.append('ec_items', convertItemsToMatomoParams(event.items));
  if (event.orderValue) params.append('ec_st', event.orderValue.toString());
  if (event.contentName) params.append('c_n', event.contentName);
  if (event.contentPiece) params.append('c_p', event.contentPiece);
  if (event.contentTarget) params.append('c_t', event.contentTarget);
  if (event.contentAction) params.append('c_i', event.contentAction);

  const url = `${process.env.REACT_APP_MATOMO_ANALYTICS_URL}/matomo.php?${params.toString()}`;
  axios
    .get(url)
    .then((res) => {
      console.log('Analytics event tracked', res);
    })
    .catch((err) => {
      console.log('Error tracking analytics event:', err);
    });
};

export const trackViewItem = (ecommerceObject: EcommerceObject) => {
  if (window?.parent && window?.parent?._paq) {
    console.log('track view item on matomo');
    const items: MatomoItem[] = parseEcommerceObject(ecommerceObject);
    for (const item of items) {
      window.parent._paq.push([
        'setEcommerceView',
        item.productSKU,
        item.productName ?? '',
        item.categoryName ?? '',
        item.price ?? 0,
      ]);
    }
    window.parent._paq.push(['trackPageView']);
  } else {
    console.log('_paq undefined');
  }
};

export const trackRemoveFromCart = (items: MatomoItem[]) => {
  if (window?.parent && window?.parent?._paq) {
    console.log('Tracking Remove from cart on matomo');
    for (const item of items) {
      window.parent._paq.push(['removeEcommerceItem', item.productSKU]);
    }
    window.parent._paq.push(['trackEcommerceCartUpdate', 0]);
  } else {
    console.log('_paq undefined');
  }
};

export const trackPurchase = (ecommerceObject: EcommerceObject) => {
  if (window?.parent && window?.parent?._paq) {
    console.log('Tracking Purchase on matomo');
    window.parent._paq.push(['trackEcommerceOrder', makeOrderId(), ecommerceObject.ecommerce.value]);
  } else {
    console.log('_paq undefined');
  }
};

const addMatomoCompatibleFields = (ecommerceObject: EcommerceObject, totalCartValue?: number) => {
  if (ecommerceObject.ecommerce.value && ecommerceObject.ecommerce.items) {
    ecommerceObject.ecommerce.items.forEach((item) => {
      item.item_category =
        typeof item.item_category === 'string'
          ? [item.item_category, item.item_source ?? '', item.added_in_store ?? '']
          : item.item_category;
    });
  }
  if (totalCartValue) {
    ecommerceObject.ecommerce.total = totalCartValue;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const pushToMatomo = (object: any, totalCartValue?: number) => {
  if (window.parent == undefined) {
    console.warn('Parent could not be found.');
    return;
  }
  if (window.parent._mtm == undefined) {
    console.warn('Matomo _mtm on parent could not be found.');
    return;
  }
  if (object.ecommerce) {
    addMatomoCompatibleFields(object, totalCartValue);
  }

  window.parent._mtm.push(object);
};

const parseEcommerceObject = (ecommerceObject: EcommerceObject): MatomoItem[] => {
  return ecommerceObject.ecommerce.items.map((item) => {
    return {
      productSKU: item.item_id,
      productName: item.item_name,
      categoryName: ecommerceObject.product_source,
      price: item.price,
      quantity: item.quantity,
    };
  });
};

const makeOrderId = () => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < 5) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};
